import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Search, ShieldCheck, ShieldOff } from '../icons';
import { useAuth } from '../context/AuthContext';
import { getVendorVerification, verifyVendor, unverifyVendor } from '../services/api';

function VerifyVendors() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const PAGE_SIZE = 50;
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [search, setSearch] = useState('');
  const [verifiedFilter, setVerifiedFilter] = useState('all'); // 'all' | 'verified' | 'unverified'
  const [stats, setStats] = useState({ total: 0, verified: 0, unverified: 0, matching: 0 });
  const [loadError, setLoadError] = useState('');

  // Searching and filtering happen on the server — there are thousands of
  // vendors, so the page loads them in slices rather than all at once.
  const params = React.useCallback((offset) => ({
    limit: PAGE_SIZE,
    offset,
    ...(search ? { search } : {}),
    ...(verifiedFilter !== 'all' ? { verified: verifiedFilter === 'verified' } : {}),
  }), [search, verifiedFilter]);

  useEffect(() => {
    // Wait for the session check to finish. Redirecting while it is still in
    // flight bounced admins to the homepage whenever they opened this URL
    // directly, since `user` is null until /auth/me resolves.
    if (authLoading) return;
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    // Debounced so typing in the search box does not fire a request per keystroke.
    const timer = setTimeout(() => {
      getVendorVerification(params(0))
        .then((res) => {
          if (cancelled) return;
          setVendors(res.data.vendors || []);
          setStats({
            total: res.data.total || 0,
            verified: res.data.verified || 0,
            unverified: res.data.unverified || 0,
            matching: res.data.matching || 0,
          });
        })
        // A failed load would otherwise read as "No vendors found"
        .catch((err) => { if (!cancelled) setLoadError(err.response?.data?.error || err.message); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, search ? 350 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [user, authLoading, navigate, params, search]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await getVendorVerification(params(vendors.length));
      setVendors((prev) => [...prev, ...(res.data.vendors || [])]);
    } catch { /* ignore */ }
    finally { setLoadingMore(false); }
  };

  // Verifying shifts the totals, so adjust them locally instead of refetching.
  const applyVerified = (id, isVerified) => {
    setVendors((prev) => prev.map(v => v.id === id ? { ...v, is_verified: isVerified } : v));
    setStats((s) => ({
      ...s,
      verified: s.verified + (isVerified ? 1 : -1),
      unverified: s.unverified + (isVerified ? -1 : 1),
    }));
  };

  const handleVerify = async (id) => {
    setActionId(id);
    try {
      await verifyVendor(id);
      applyVerified(id, true);
    } catch {
      alert('Failed to verify vendor');
    } finally {
      setActionId(null);
    }
  };

  const handleUnverify = async (id) => {
    setActionId(id);
    try {
      await unverifyVendor(id);
      applyVerified(id, false);
    } catch {
      alert('Failed to unverify vendor');
    } finally {
      setActionId(null);
    }
  };

  const filtered = vendors;
  const totalCount = stats.total;
  const verifiedCount = stats.verified;
  const unverifiedCount = stats.unverified;

  if (authLoading) return <div className="loading-page"><div className="spinner" /></div>;
  if (!user || user.role !== 'admin') return null;

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-header">
          <div className="admin-header-left">
            <ShieldCheck size={28} className="admin-icon" />
            <div>
              <h1>Verify / Unverify Vendors</h1>
              <p>Manage vendor verification badges. Verified vendors show a trust badge on their listing.</p>
            </div>
          </div>
          <button className="btn btn-outline" onClick={() => navigate('/admin')}>
            ← Back to Admin
          </button>
        </div>

        <div className="verify-stats-grid">
          <div className="verify-stat-card">
            <span className="verify-stat-number">{totalCount.toLocaleString()}</span>
            <span className="verify-stat-label">Total Vendors</span>
          </div>
          <div className="verify-stat-card verified">
            <span className="verify-stat-number">{verifiedCount.toLocaleString()}</span>
            <span className="verify-stat-label">Verified</span>
          </div>
          <div className="verify-stat-card unverified">
            <span className="verify-stat-number">{unverifiedCount.toLocaleString()}</span>
            <span className="verify-stat-label">Unverified</span>
          </div>
        </div>

      <div className="admin-section">
        <div className="admin-filters" style={{ marginBottom: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="admin-search" style={{ flex: 1, minWidth: '220px' }}>
            <Search size={16} />
            <input
              type="text"
              placeholder="Search by name, email, or city..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="admin-status-filters">
            <button className={`status-btn ${verifiedFilter === 'all' ? 'active' : ''}`} onClick={() => setVerifiedFilter('all')}>All</button>
            <button className={`status-btn approved ${verifiedFilter === 'verified' ? 'active' : ''}`} onClick={() => setVerifiedFilter('verified')}>
              <CheckCircle size={13} /> Verified
            </button>
            <button className={`status-btn pending ${verifiedFilter === 'unverified' ? 'active' : ''}`} onClick={() => setVerifiedFilter('unverified')}>
              <XCircle size={13} /> Unverified
            </button>
          </div>
        </div>

        {loading ? (
          <div className="tab-loading"><div className="spinner" /></div>
        ) : loadError ? (
          <div className="alert alert-error">Could not load vendors: {loadError}</div>
        ) : (
          <div className="admin-vendor-table">
            <table>
              <thead>
                <tr>
                  <th>Business Name</th>
                  <th>Category</th>
                  <th>City</th>
                  <th>Rating</th>
                  <th>Approval</th>
                  <th>Verified Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((v) => (
                  <tr key={v.id} className={`vendor-row ${v.approval_status}`}>
                    <td>
                      <div className="vendor-name-cell">
                        <strong>{v.business_name}</strong>
                        <small>{v.email || v.owner_email}</small>
                      </div>
                    </td>
                    <td>{v.category_name || '—'}</td>
                    <td>{v.city || '—'}</td>
                    <td>{v.rating ? `★ ${v.rating}` : '—'}</td>
                    <td>
                      <span className={`approval-badge small ${v.approval_status}`}>{v.approval_status}</span>
                    </td>
                    <td>
                      {v.is_verified ? (
                        <span style={{ color: '#16a34a', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <ShieldCheck size={15} /> Verified
                        </span>
                      ) : (
                        <span style={{ color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <ShieldOff size={15} /> Unverified
                        </span>
                      )}
                    </td>
                    <td>
                      {v.is_verified ? (
                        <button
                          className="action-btn reject"
                          onClick={() => handleUnverify(v.id)}
                          disabled={actionId === v.id}
                          style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                          <ShieldOff size={13} />
                          {actionId === v.id ? 'Updating...' : 'Unverify'}
                        </button>
                      ) : (
                        <button
                          className="action-btn approve"
                          onClick={() => handleVerify(v.id)}
                          disabled={actionId === v.id}
                          style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                          <ShieldCheck size={13} />
                          {actionId === v.id ? 'Updating...' : 'Verify'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan="7" className="no-data">No vendors found</td></tr>
                )}
              </tbody>
            </table>
            {filtered.length > 0 && (
              <div className="verify-table-footer">
                <span>Showing {filtered.length.toLocaleString()} of {stats.matching.toLocaleString()}</span>
                {filtered.length < stats.matching && (
                  <button className="btn btn-outline" onClick={loadMore} disabled={loadingMore}>
                    {loadingMore ? 'Loading…' : `Load ${Math.min(PAGE_SIZE, stats.matching - filtered.length)} more`}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

export default VerifyVendors;
